import { Store } from '@/redux';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { LoggerService } from '@/shared/logger';
import { App } from '@capacitor/app';
import { PluginListenerHandle } from '@capacitor/core';

export type SendMessage = (message: string) => void;

/**
 * connecting   – first connection attempt in flight
 * open         – socket is live
 * reconnecting – dropped, retrying automatically
 * failed       – gave up after retries; call `reconnect()` to try again
 */
export type SocketConnectionState =
  | 'connecting'
  | 'open'
  | 'reconnecting'
  | 'failed';

const MAX_RETRIES = 5;

export const useWebsocket = <T = unknown>(websocketURL: string) => {
  const baseURL = useRef<string>(websocketURL);
  const { token } = useSelector((state: Store) => state.userReducer);
  const socketURL = useMemo(
    () => addTokenToWebsocketURL(baseURL.current, token),
    [token]
  );

  const websocketRef = useRef<WebSocket | null>(null);
  const disposedRef = useRef(false);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [socketError, setSocketError] = useState(false);
  const [socketResponse, setResponse] = useState<T | null>(null);
  const [connectionState, setConnectionState] =
    useState<SocketConnectionState>('connecting');

  const connectWebSocket = useCallback(
    (socketURL: string | null, retries = 0) => {
      if (!socketURL) {
        LoggerService.error('Websocket URL not found...');
        return;
      }
      if (retryTimer.current) {
        clearTimeout(retryTimer.current);
        retryTimer.current = null;
      }

      const socket = new WebSocket(socketURL);
      websocketRef.current = socket;
      // Events from a socket we already replaced or disposed are ignored, so
      // a stale close never spawns a second connection.
      const isCurrent = () =>
        websocketRef.current === socket && !disposedRef.current;

      socket.onopen = () => {
        if (!isCurrent()) return;
        LoggerService.info('Connected to websocket:: ', {
          socketURL: baseURL.current,
        });
        socket.send('connected');
        setSocketError(false);
        setConnectionState('open');
      };

      socket.onmessage = event => {
        if (!isCurrent()) return;
        try {
          const data = JSON.parse(event.data);
          setResponse(data);
        } catch (e) {
          LoggerService.warn('Ignoring non JSON websocket message');
        }
      };

      socket.onerror = error => {
        LoggerService.error('Error in websocket connection socket', {
          error,
          socketURL: baseURL.current,
        });
      };

      socket.onclose = e => {
        if (!isCurrent()) return;
        LoggerService.info(
          'Websocket connection closed, attempting to reconnect...'
        );

        if (e.code === 1000) {
          LoggerService.info('Websocket connection closed successfully');
          return;
        }

        if (retries > MAX_RETRIES) {
          LoggerService.warn('Websocket connection failed after 5 retries');
          setSocketError(true);
          setConnectionState('failed');
          return;
        }

        setConnectionState('reconnecting');
        const next = retries + 1;
        // short linear backoff so a flaky network does not burn all retries
        retryTimer.current = setTimeout(
          () => connectWebSocket(socketURL, next),
          Math.min(next * 400, 2400)
        );
      };
    },
    []
  );

  useEffect(() => {
    if (!socketURL) {
      LoggerService.error('Websocket URL not found...');
      return;
    }

    disposedRef.current = false;
    const existing = websocketRef.current;
    if (
      existing &&
      (existing.readyState === WebSocket.OPEN ||
        existing.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }

    connectWebSocket(socketURL);

    return () => {
      disposedRef.current = true;
      if (retryTimer.current) clearTimeout(retryTimer.current);
      if (websocketRef.current) {
        websocketRef.current.close(1000);
      }
    };
  }, [connectWebSocket, socketURL]);

  /** Manual retry after `failed` (e.g. a "Reconnect" button). */
  const reconnect = useCallback(() => {
    if (!socketURL) return;
    const ws = websocketRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) return;
    if (ws) {
      websocketRef.current = null; // detach first so its close is ignored
      ws.close(1000);
    }
    disposedRef.current = false;
    setConnectionState('reconnecting');
    connectWebSocket(socketURL);
  }, [connectWebSocket, socketURL]);

  // Coming back online: retry immediately instead of waiting for the user.
  useEffect(() => {
    const onOnline = () => {
      const ws = websocketRef.current;
      if (!ws || ws.readyState === WebSocket.CLOSED) {
        reconnect();
      }
    };
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [reconnect]);

  const sendMessage = useCallback((message: string) => {
    if (
      !websocketRef.current ||
      websocketRef.current.readyState !== WebSocket.OPEN
    ) {
      LoggerService.error('Websocket not connected :(');
      return;
    }
    websocketRef.current.send(message);
  }, []);

  useEffect(() => {
    let pauseListener: Promise<PluginListenerHandle> | undefined;
    let resumeListener: Promise<PluginListenerHandle> | undefined;

    // Capacitor app lifecycle handlers
    const setupCapacitorListeners = async () => {
      try {
        pauseListener = App.addListener('pause', () => {
          LoggerService.info('Capacitor app paused - closing websocket');
          websocketRef.current?.close(1000);
        });

        resumeListener = App.addListener('resume', () => {
          LoggerService.info('Capacitor app resumed - reconnecting');
          connectWebSocket(socketURL);
        });
      } catch (error) {
        LoggerService.warn(
          'Capacitor not available, using browser events only'
        );
      }
    };

    setupCapacitorListeners();

    return () => {
      pauseListener?.then(listener => listener.remove());
      resumeListener?.then(listener => listener.remove());
    };
  }, [connectWebSocket, socketURL]);

  return {
    socketResponse,
    ws: websocketRef.current,
    socketError,
    sendMessage,
    connectionState,
    reconnect,
  };
};

function addTokenToWebsocketURL(url: string, token: string | null) {
  if (!token) {
    return null;
  }
  return `${url}?token=Bearer ${token}`;
}
